<template>
  <div>
    <div class="d-flex align-center flex-wrap ga-3 mb-4">
      <div class="text-h6">LLM Usage</div>
      <v-spacer />
      <v-select
        v-model="rangeDays"
        :items="rangeOptions"
        item-title="label"
        item-value="value"
        density="compact"
        variant="outlined"
        hide-details
        style="max-width: 200px"
        @update:modelValue="fetchUsage"
      />
    </div>

    <div class="text-caption text-medium-emphasis mb-4">
      AI token consumption for your organization. Tracking began when usage
      instrumentation was deployed; earlier periods show no data.
    </div>

    <div v-if="loading" class="d-flex justify-center pa-8">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <div v-else-if="rows.length === 0" class="text-center pa-8 text-medium-emphasis">
      <v-icon size="40" class="mb-2 text-disabled">mdi-chart-box-outline</v-icon>
      <div>No AI usage recorded for this organization in the selected range.</div>
    </div>

    <template v-else>
      <!-- KPI cards (no cost — that's an internal, admin-only figure) -->
      <div class="kpi-grid mb-6">
        <v-card variant="outlined" class="pa-4">
          <div class="text-caption text-medium-emphasis">Total tokens</div>
          <div class="text-h5 font-weight-medium">{{ formatTokens(totals.tokens) }}</div>
        </v-card>
        <v-card variant="outlined" class="pa-4">
          <div class="text-caption text-medium-emphasis">AI calls</div>
          <div class="text-h5 font-weight-medium">{{ totals.calls.toLocaleString() }}</div>
        </v-card>
        <v-card variant="outlined" class="pa-4">
          <div class="text-caption text-medium-emphasis">Active users</div>
          <div class="text-h5 font-weight-medium">{{ totals.users }}</div>
        </v-card>
      </div>

      <v-card variant="outlined" class="pa-4">
        <div class="text-subtitle-2 font-weight-bold mb-3">Tokens by model</div>
        <div v-for="row in byModel" :key="row.key" class="bar-row">
          <div class="bar-label text-truncate" :title="modelDisplayName(row.key)">{{ modelDisplayName(row.key) }}</div>
          <div class="bar-track">
            <div class="bar-fill" :style="{ width: barWidth(row.tokens, maxModelTokens) }" />
          </div>
          <div class="bar-value">{{ formatTokens(row.tokens) }}</div>
        </div>
      </v-card>
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { useOrgUsage } from '@/composables/useOrgUsage'
import { formatTokens, barWidth } from '@/utils/usageFormat'
import { modelDisplayName } from '@/constants/aiModels'

const props = defineProps({
  orgId: { type: String, required: true }
})

const { rangeDays, rangeOptions, rows, loading, totals, tokensGroupedBy, load, fetchUsage } = useOrgUsage(props.orgId)

onMounted(load)

const byModel = computed(() => tokensGroupedBy((r) => r.model))
const maxModelTokens = computed(() => Math.max(...byModel.value.map((x) => x.tokens), 1))
</script>

<style scoped>
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
}
.bar-row {
  display: grid;
  grid-template-columns: 130px 1fr 64px;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}
.bar-label {
  font-size: 13px;
}
.bar-track {
  background: rgba(var(--v-theme-on-surface), 0.06);
  border-radius: 4px;
  height: 10px;
  overflow: hidden;
}
.bar-fill {
  height: 100%;
  background: rgb(var(--v-theme-primary));
  border-radius: 4px;
}
.bar-value {
  font-size: 13px;
  text-align: right;
  font-variant-numeric: tabular-nums;
}
</style>
